import { CircleInfoIcon } from '@components/Icons/CircleInfoIcon';
import { formatBytesToString, formatDurationToString, pluralize } from '@lib/utils/units';
import { IconButton, Skeleton, Tooltip } from '@mui/material';
import { useMediaData } from '@pages/Library/components/LibraryContentMediaTable/hooks';
import { LibraryFailedMediaModal } from '@pages/Library/components/ListModals/LibraryFailedMediaModal/LibraryFailedMediaModal';
import { useMediaTable } from '@pages/Library/context/MediaTableContext';
import { isProbablyImage } from '@pages/Library/utils/mediaStatus';
import { matchesOmniFilter, parseOmniQuery } from '@pages/Library/utils/omniFilter';
import { useLibrary } from '@stores/Library/Library';
import { useLibraryFolder } from '@stores/Library/LibraryFolder';
import { omniFilterPreferences } from '@stores/OmniFilterPreferences';
import { useUIPreferences } from '@stores/UIPreferences';
import { useUploadManager } from '@stores/UploadManager/UploadManager';
import { observer } from 'mobx-react';
import { useState } from 'react';
import css from './LibraryToolbar.module.scss';

/**
 * Uploads into THIS library, whichever folder they go to (queued + uploading, not success): a
 * dropped directory uploads into folders the user is not looking at.
 */
export function useLibraryUploadingCount() {
  const uploads = useUploadManager();
  const libraryId = useLibraryFolder().library?.libraryId ?? null;
  return libraryId == null
    ? 0
    : Object.values(uploads.uploadStatus).filter(
        (s) =>
          (s.status === 'queued' || s.status === 'uploading') &&
          s.destination.libraryId === libraryId,
      ).length;
}

/**
 * What the view holds — "2 folders • 73 media files • 10h • 11GB", and the failed count — on the
 * search line, beside the search that narrows it. The counts follow the search and the table's
 * filters.
 */
export const LibraryStats = observer(() => {
  const library = useLibrary();
  const uploads = useUploadManager();
  const folder = useLibraryFolder();
  // The folders the search leaves, as the media count is the media it leaves.
  const childFolderCount = folder.visibleChildFolders.length;
  const uiPreferences = useUIPreferences();
  const loaded = library.name !== undefined;
  const [failedModalOpen, setFailedModalOpen] = useState(false);

  // Get media data for duration calculation in list view
  const { data: mediaData } = useMediaData();

  // Get filtered row count, duration, and size from context (only available in list view)
  let filteredRowCount: number | null = null;
  let filteredDuration: number | null = null;
  let filteredSize: number | null = null;
  try {
    const mediaTable = useMediaTable();
    filteredRowCount = mediaTable.filteredRowCount;
    filteredDuration = mediaTable.filteredDuration;
    filteredSize = mediaTable.filteredSize;
  } catch {
    // Context not available (e.g., in grid view), that's fine
  }

  // Count active uploads that match the current title filter (for gallery view parity with list view)
  const parsedOmni = parseOmniQuery(folder._omniFilter, omniFilterPreferences.matchAll);
  const filteredUploadCount = Object.values(uploads.uploadStatus).filter((s) => {
    if (s.status !== 'queued' && s.status !== 'uploading' && s.status !== 'success') return false;
    if (!folder.holdsUpload(s.destination)) return false;
    // Dedup: skip if backend media already exists and is past "empty"
    if (s.media?._id && folder.files.some((f) => f._id === s.media?._id && f.status !== 'empty'))
      return false;
    // Apply the omnifilter (uploads only have a filename + maybe a backend id)
    if (parsedOmni && !matchesOmniFilter({ name: s.file.name, mediaId: s.media?._id }, parsedOmni))
      return false;
    return true;
  }).length;

  // Use different counts based on view mode:
  // - List view: filtered count from DataTable (reflects applied filters)
  // - Grid view: count from store (folder.filesCountDisplay) + filtered uploads
  const mediaCount =
    uiPreferences.libraryViewMode === 'list'
      ? (filteredRowCount ?? mediaData.length) // Fallback to mediaData.length if filtered count not available yet
      : folder.filesCountDisplay + filteredUploadCount;

  const uploadingCount = useLibraryUploadingCount();

  const failedCount = folder.filesFailedOrAbandoned.length + uploads.rejectedFiles.length;

  // Calculate duration based on view mode and filters
  const duration =
    uiPreferences.libraryViewMode === 'list'
      ? // List view: use filtered duration from DataTable
        (filteredDuration ??
        mediaData.reduce(
          (acc, media) => acc + (isProbablyImage(media) ? 0 : media.metadata?.video?.duration || 0),
          0,
        ))
      : // Grid view: the library's own stats when the view is the whole library, otherwise
        // what the store holds for the view (a folder, or the omnifilter's matches)
        folder._omniFilter || !folder.showsWholeLibrary
        ? folder.filesFilteredDuration
        : library.stats?.mediaDuration;

  // Format stats for display
  const formattedDuration = duration
    ? formatDurationToString(duration, { spaceBeforeUnit: false })
    : null;

  const sizeInBytes =
    uiPreferences.libraryViewMode === 'list'
      ? (filteredSize ??
        mediaData.reduce((acc, media) => acc + (media.metadata?.video?.originalMediaSize || 0), 0))
      : folder._omniFilter || !folder.showsWholeLibrary
        ? folder.filesFilteredSize
        : library.stats?.mediaSize;

  const formattedSize = sizeInBytes
    ? formatBytesToString(sizeInBytes * 1000 * 1000, { spaceBeforeUnit: false })
    : null;

  return (
    <>
      {/* Every conditional below wraps its content in an ELEMENT rather than leaving bare
      text as the subtree root, and the whole block opts out of machine translation.
      Both exist for the same crash.

      Chrome's page translator rewrites text nodes by wrapping them in `<font>`, which
      re-parents them. React keeps the original text node as the fiber's stateNode, so
      when a conditional unmounts it calls `extraInfo.removeChild(textNode)` on a node
      whose parent is now the `<font>` — `NotFoundError: The node to be removed is not a
      child of this node`, which took the entire library page down through the page's
      error boundary. `failedCount` dropping to 0 is the common trigger, because
      retrying failed uploads splices `uploads.rejectedFiles`.

      Translate re-parents text nodes but NOT elements, so an element root is safe even
      if the opt-out is ignored (other extensions, a translated subframe). Keep it that
      way: no bare text as the root of a conditional in here.

      Which makes `translate="no"` redundant against the CRASH — it is here for a second
      problem. Every string in this block is a live counter, and a translator that has
      replaced a text node with a `<font>` sibling leaves React writing updates into a
      node that is no longer displayed. The count then freezes at whatever it read when
      the page was translated. A stale "3 failed" that never moves is worse than an
      untranslated one, so these specific strings stay in English.

      Scoped to this block and the header's processing lines only, NOT the page: everything
      else (the library name, the ⋯ menu, the verb pills, and the errored-media tooltip,
      which MUI renders through a portal to `document.body` and is therefore outside
      this subtree) stays translatable. */}
      <div className={css.extraInfo} translate="no">
        {loaded ? (
          <span>
            {childFolderCount > 0 && (
              <span className={css.statFolders}>{pluralize(childFolderCount, 'folder')} • </span>
            )}
            {pluralize(mediaCount, 'media file')}
            {formattedDuration && <span className={css.statDuration}> • {formattedDuration}</span>}
            {formattedSize && (
              <span className={css.statSize}>
                {' '}
                • {uploadingCount > 0 && <span className={css.approximateSign}>≥ </span>}
                {formattedSize}
              </span>
            )}
          </span>
        ) : (
          <Skeleton variant="text" height="100%" width="10ch" />
        )}
        {failedCount > 0 && (
          <>
            <span>•</span>
            {loaded ? (
              <span
                onClick={() => setFailedModalOpen(true)}
                style={{ cursor: 'pointer' }}
                className={css.failedSpan}
              >
                {failedCount} failed
                <Tooltip title="Media that have errored. Click for more info.">
                  <IconButton
                    size="small"
                    className={css.iconButton}
                    onClick={() => setFailedModalOpen(true)}
                  >
                    <CircleInfoIcon />
                  </IconButton>
                </Tooltip>
              </span>
            ) : (
              <Skeleton variant="text" height="100%" width="10ch" />
            )}
          </>
        )}
      </div>
      <LibraryFailedMediaModal open={failedModalOpen} onClose={() => setFailedModalOpen(false)} />
    </>
  );
});
